/**
 * UISwitch — GOD-gated dispatcher between TechUI (procedural) and SpriteUI (pixel art).
 *
 * When GOD.useSpriteUI is true, factories delegate to SpriteUI (NineSlice-based).
 * When false (default), they delegate to TechUI (Graphics-based).
 *
 * Callers import from UISwitch instead of TechUI/SpriteUI directly.
 * This keeps migration gradual and fully reversible.
 */

import Phaser from 'phaser';
import { GOD } from '@/config/GodMode';
import { createTechButton, createSmallTechButton, type TechButtonOptions } from '@/ui/TechUI';
import { createSpriteButton, createSmallSpriteButton, createSpritePanel, createSpriteBar, createSpriteBanner, createSpriteSlot, createSpriteIconFrame } from '@/ui/SpriteUI';
import type { SpritePanelType, SpriteBarType, SpriteBannerType } from '@/ui/SpriteUI';

// ── Button ──

export function createButton(
  scene: Phaser.Scene,
  x: number, y: number,
  label: string,
  opts: TechButtonOptions,
): Phaser.GameObjects.Container {
  return GOD.useSpriteUI
    ? createSpriteButton(scene, x, y, label, opts)
    : createTechButton(scene, x, y, label, opts);
}

export function createSmallButton(
  scene: Phaser.Scene,
  x: number, y: number,
  label: string,
  color: number,
  onClick: () => void,
  depth = 2,
): Phaser.GameObjects.Container {
  return GOD.useSpriteUI
    ? createSmallSpriteButton(scene, x, y, label, color, onClick, depth)
    : createSmallTechButton(scene, x, y, label, color, onClick, depth);
}

// ── Panel (sprite-only, falls back to nothing in TechUI mode) ──

export function createPanel(
  scene: Phaser.Scene,
  x: number, y: number,
  w: number, h: number,
  panelType: SpritePanelType = 'large',
  depth = 1,
): Phaser.GameObjects.NineSlice | null {
  if (!GOD.useSpriteUI) return null;
  return createSpritePanel(scene, x, y, w, h, panelType, depth);
}

// ── Bar frame (sprite-only) ──

export function createBarFrame(
  scene: Phaser.Scene,
  x: number, y: number,
  w: number,
  barType: SpriteBarType = 'hp',
  depth = 1,
): Phaser.GameObjects.NineSlice | null {
  if (!GOD.useSpriteUI) return null;
  return createSpriteBar(scene, x, y, w, barType, depth);
}

// ── Banner (sprite-only) ──

export function createBanner(
  scene: Phaser.Scene,
  x: number, y: number,
  bannerType: SpriteBannerType,
  depth = 5,
): Phaser.GameObjects.Image | null {
  if (!GOD.useSpriteUI) return null;
  return createSpriteBanner(scene, x, y, bannerType, depth);
}

// ── Slot (sprite-only) ──

export function createSlot(
  scene: Phaser.Scene,
  x: number, y: number,
  locked = false,
  depth = 1,
): Phaser.GameObjects.Image | null {
  if (!GOD.useSpriteUI) return null;
  return createSpriteSlot(scene, x, y, locked, depth);
}

// ── Icon frame (sprite-only) ──

export function createIconFrame(
  scene: Phaser.Scene,
  x: number, y: number,
  depth = 1,
): Phaser.GameObjects.Image | null {
  if (!GOD.useSpriteUI) return null;
  return createSpriteIconFrame(scene, x, y, depth);
}
